import axios, {AxiosError, AxiosRequestConfig, AxiosResponse} from 'axios'
import {AuthToken} from './model'
import axiosRetry, {isNetworkOrIdempotentRequestError} from 'axios-retry'
import {getMidwayHelper} from './midway'

const API_ROOT = process.env.REACT_APP_API_ROOT || `${window.location.protocol}//api.${window.location.hostname}`;

const API_CONFIG: AxiosRequestConfig = {
    baseURL: `${API_ROOT}/api`,
    headers: {
        'Content-Type': 'application/json'
    }
}

export const apiClient = axios.create(API_CONFIG)

/**
 * Whenever an API call responds with status code 403, that indicates there's something wrong with the user's credentials.
 * Reload the page so that the user can reauthenticate with Midway.
 */
function handleApiError(error: AxiosError) {
    if (error.response?.status === 403) {
        window.location.reload()
    }
    throw error
}

/**
 * Request the user's Midway token from the server, so that we can hold a copy of it and use it in requests to authenticated endpoints.
 * The request to GET /token will include the user's Midway token in a cookie that Midway has installed.  If the cookie is not sent in
 * this request (perhaps the cookie has expired in the browser), we'll get an HTTP 403 response, in which case we reload the page so that
 * the user can reauthenticate with Midway and get a fresh cookie.
 */
async function fetchAuthToken(): Promise<string | undefined> {
    try {
        const response: AxiosResponse<AuthToken> = await axios.get(`${API_ROOT}/token`, {
            withCredentials: true
        })
        return response.data.token
    } catch (error: any) {
        handleApiError(error)
    }
}

// Get the auth token immediately when the app starts.  We don't want to wait - the user's Midway cookie might expire
export const authToken = process.env.REACT_APP_AUTH_TOKEN || getMidwayHelper().getToken()

apiClient.interceptors.request.use(async (config) => {
    // authToken is either a string or a Promise<string>.  We can 'await' both these types.
    // Fetching the auth token should only be done once.
    const token = await authToken
    config.headers['Authorization'] = `Bearer ${token}`
    return config
});

apiClient.interceptors.response.use(response => response, handleApiError)

function isRetryableError(error: AxiosError): boolean {
    return isNetworkOrIdempotentRequestError(error) || error.response?.status === 500
}

axiosRetry(apiClient, {
    retryDelay: axiosRetry.exponentialDelay,
    retryCondition: isRetryableError
})
